import { isBaseTradeProofHostname, isWildcardTenantHostname, normalizeTenantHostname } from '../whiteLabel';

export type TenantHostClass = 'base' | 'tenant_wildcard' | 'tenant_candidate';

/**
 * Classify a hostname for tenant-host routing:
 *  - 'base'              -> Trade-Proof base hosts that may show the marketing site
 *  - 'tenant_wildcard'   -> {slug}.tradeproof.app / {slug}.localhost / {slug}.local
 *  - 'tenant_candidate'  -> any other non-base hostname (e.g. a verified custom domain)
 */
export function classifyTenantHostname(hostname: string): TenantHostClass {
  const normalized = normalizeTenantHostname(hostname);
  if (!normalized) return 'base';
  if (isBaseTradeProofHostname(normalized)) return 'base';
  if (isWildcardTenantHostname(normalized)) return 'tenant_wildcard';
  return 'tenant_candidate';
}

/**
 * Public marketing paths that must never render on a tenant host. Root `/` is the
 * canonical redirect target; the other marketing shell pages are also redirected to
 * /login so a tenant hostname never shows the Trade-Proof marketing site.
 *
 * Valid tenant routes (/login, /dashboard, /calendar, /trades, /journal,
 * /admin/tools, ...) and auth flows (/auth/callback, /forgot-password,
 * /auth/reset-password) are never redirected here.
 */
export function isTenantHostMarketingPath(pathname: string): boolean {
  const normalized = String(pathname || '/').trim() || '/';
  const trimmed = normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;

  if (trimmed === '/') return true;
  if (trimmed === '/help' || trimmed.startsWith('/help/')) return true;
  if (
    trimmed === '/legal'
    || trimmed === '/terms'
    || trimmed === '/terms-of-service'
    || trimmed === '/privacy'
    || trimmed === '/privacy-policy'
  ) {
    return true;
  }
  if (trimmed === '/security' || trimmed === '/feature-request') return true;
  return false;
}
